import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useQuery } from '@tanstack/react-query';
import { Link2 } from 'lucide-react-native';
import { useMemo } from 'react';
import { Pressable } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import useClipboard from '@/hooks/useClipboard';
import { type InquiryV2 } from '@/services/getInquiriesV2';
import { getInquiryV2QueryOptions } from '@/services/getInquiryV2';
import { segmentClient } from '@/util/analytics';
import { isApiErrorV2 } from '@/util/fetchClient';
import { getInquirySubject } from '@/util/getInquirySubject';
import { getProfileNameAndInitials } from '@/util/getProfileNameAndInitials';

type BaseProps = {
  onView?: (conversationId: string) => void;
  label?: string;
  containerClassName?: string;
  textClassName?: string;
  /** When true, View sits next to content (justify-start) instead of far right */
  groupViewWithContent?: boolean;
  /** When true, single flowing line: "Linked from: X — Author → Recipient  View" (wraps on narrow) */
  compact?: boolean;
};

/** Mock data for demo/compare mode — skips API fetch when passed to SingleModeProps */
export type LinkedInquiryBannerDemoData = {
  title?: { cancerFocus?: string; age?: string; diagnosis?: string };
  author?: { firstName?: string; lastName?: string; identifiesAsDoctor?: boolean };
  recipient?: { firstName?: string; lastName?: string; identifiesAsDoctor?: boolean };
};

type SingleModeProps = BaseProps & {
  conversationId: string;
  inquiries?: never;
  loggedInUserId?: number | string;
  alwaysShowView?: boolean;
  /** When provided, skips API fetch and uses this data (for demos) */
  demoData?: LinkedInquiryBannerDemoData;
};

type MultipleModeProps = BaseProps & {
  inquiries: InquiryV2[];
  conversationId?: never;
  loggedInUserId?: never;
  alwaysShowView?: never;
};

type Props = SingleModeProps | MultipleModeProps;

function isSingleMode(props: Props): props is SingleModeProps {
  return 'conversationId' in props && !!props.conversationId;
}

export function LinkedInquiryBanner(props: Props) {
  const { copyToClipboard } = useClipboard();

  if (isSingleMode(props)) {
    return (
      <SingleBanner
        conversationId={props.conversationId}
        loggedInUserId={props.loggedInUserId}
        onView={props.onView}
        label={props.label ?? 'Linked from'}
        alwaysShowView={props.alwaysShowView ?? false}
        containerClassName={props.containerClassName}
        textClassName={props.textClassName}
        groupViewWithContent={props.groupViewWithContent}
        compact={props.compact}
        demoData={props.demoData}
      />
    );
  }

  if (props.inquiries.length === 0) {
    return null;
  }

  return (
    <VStack
      className={
        props.containerClassName ?? 'border-y border-outline-100 bg-background-0 px-7 py-2'
      }>
      {props.inquiries.map((inq) => {
        const conversationId = inq.conversationId;

        if (!conversationId) {
          return null;
        }

        const subject = getInquirySubject(inq.title as InquiryV2['title']);

        const authorName = inq.author
          ? getProfileNameAndInitials({
              firstName: inq.author.firstName,
              lastName: inq.author.lastName,
              identifiesAsDoctor: inq.author.identifiesAsDoctor,
            }).name
          : undefined;

        const recipientName = inq.recipient
          ? getProfileNameAndInitials({
              firstName: inq.recipient.firstName,
              lastName: inq.recipient.lastName,
              identifiesAsDoctor: inq.recipient.identifiesAsDoctor,
            }).name
          : undefined;

        const handlePress = () => {
          segmentClient.track(ANALYTICS_EVENTS.LINKED_INQUIRY_BANNER_TRIGGERED, {
            conversationId,
            action: 'navigate',
          });

          props.onView?.(conversationId);
        };

        return (
          <Pressable
            key={conversationId}
            onPress={handlePress}
            className="active:opacity-80"
            accessibilityRole="button"
            accessibilityLabel={`View linked conversation: ${subject || conversationId}`}>
            <HStack className="items-center gap-3 py-1 first:pt-0 last:pb-0">
              <Icon as={Link2} size="md" className="text-typography-600" />
              <VStack className="min-w-30 flex-1">
                <HStack className="flex-row flex-wrap items-baseline gap-x-1">
                  <Text size="sm" className="text-typography-600">
                    {props.label ?? 'Linked to'}:
                  </Text>
                  <Text size="sm" className="font-medium text-typography-800">
                    {subject || conversationId}
                  </Text>
                </HStack>
                {authorName && recipientName && (
                  <HStack className="items-center gap-1">
                    <Text size="sm" className="text-typography-700">
                      {authorName}
                    </Text>
                    <Text size="sm" className="text-typography-500">
                      →
                    </Text>
                    <Text size="sm" className="text-typography-700">
                      {recipientName}
                    </Text>
                  </HStack>
                )}
              </VStack>
            </HStack>
          </Pressable>
        );
      })}
    </VStack>
  );
}

function SingleBanner({
  conversationId,
  loggedInUserId,
  onView,
  label,
  alwaysShowView,
  containerClassName,
  textClassName,
  groupViewWithContent,
  compact,
  demoData,
}: {
  conversationId: string;
  loggedInUserId?: number | string;
  onView?: (conversationId: string) => void;
  label: string;
  alwaysShowView: boolean;
  containerClassName?: string;
  textClassName?: string;
  groupViewWithContent?: boolean;
  compact?: boolean;
  demoData?: LinkedInquiryBannerDemoData;
}) {
  const { copyToClipboard } = useClipboard();

  const query = useQuery({
    ...getInquiryV2QueryOptions({ conversationId }),
    enabled: !demoData,
  });

  const subject = useMemo(() => {
    if (demoData?.title) {
      return getInquirySubject(demoData.title as InquiryV2['title']);
    }

    const title = query.data?.title as InquiryV2['title'] | undefined;

    return title ? getInquirySubject(title) : undefined;
  }, [demoData?.title, query.data?.title]);

  const shortId = useMemo(
    () => (conversationId ? `${conversationId.slice(0, 6)}…${conversationId.slice(-4)}` : ''),
    [conversationId]
  );

  const canView = useMemo(() => {
    if (demoData) {
      return true;
    }

    if (!loggedInUserId || !query.data) {
      return false;
    }

    const authorId = query.data.author?.id;

    const recipientId = query.data.recipient?.id;

    const uid = typeof loggedInUserId === 'string' ? Number(loggedInUserId) : loggedInUserId;

    return uid === authorId || uid === recipientId;
  }, [demoData, loggedInUserId, query.data]);

  const participants = useMemo(() => {
    if (demoData?.author && demoData?.recipient) {
      return {
        authorName: getProfileNameAndInitials({
          firstName: demoData.author.firstName,
          lastName: demoData.author.lastName,
          identifiesAsDoctor: demoData.author.identifiesAsDoctor,
        }).name,
        recipientName: getProfileNameAndInitials({
          firstName: demoData.recipient.firstName,
          lastName: demoData.recipient.lastName,
          identifiesAsDoctor: demoData.recipient.identifiesAsDoctor,
        }).name,
      };
    }

    if (!query.data?.author || !query.data?.recipient) {
      return undefined;
    }

    return {
      authorName: getProfileNameAndInitials({
        firstName: query.data.author.firstName,
        lastName: query.data.author.lastName,
        identifiesAsDoctor: query.data.author.identifiesAsDoctor,
      }).name,
      recipientName: getProfileNameAndInitials({
        firstName: query.data.recipient.firstName,
        lastName: query.data.recipient.lastName,
        identifiesAsDoctor: query.data.recipient.identifiesAsDoctor,
      }).name,
    };
  }, [demoData, query.data?.author, query.data?.recipient]);

  const refErrorMessage = useMemo(() => {
    const err = query.error as unknown;

    if (err && isApiErrorV2(err)) {
      if (err.status === 403) {
        return "You don't have permission to view that inquiry.";
      }

      if (err.status === 404) {
        return 'Linked inquiry not found.';
      }
    }

    return 'Unable to open linked inquiry.';
  }, [query.error]);

  const isReady = query.isSuccess || !!demoData;

  const participantsSection =
    participants && isReady ? (
      <HStack className="items-center gap-1">
        <Text size="sm" className="text-typography-700">
          {participants.authorName}
        </Text>
        <Text size="sm" className="text-typography-500">
          →
        </Text>
        <Text size="sm" className="text-typography-700">
          {participants.recipientName}
        </Text>
      </HStack>
    ) : null;

  const handlePress = () => {
    const action = onView && (canView || alwaysShowView) ? 'navigate' : 'copy';

    segmentClient.track(ANALYTICS_EVENTS.LINKED_INQUIRY_BANNER_TRIGGERED, {
      conversationId,
      action,
    });

    if (onView && (canView || alwaysShowView)) {
      onView(conversationId);
    } else {
      copyToClipboard(conversationId);
    }
  };

  const errorSection = query.isError ? (
    <VStack className="ml-3">
      <Text size="sm" className="text-error-700">
        {refErrorMessage}
      </Text>
    </VStack>
  ) : null;

  if (compact) {
    const participantsStr =
      participants && isReady ? `${participants.authorName} → ${participants.recipientName}` : null;

    return (
      <Pressable
        onPress={handlePress}
        className={twJoin(
          containerClassName ?? 'flex-wrap items-center gap-x-1.5 gap-y-1 px-3 py-2',
          'active:opacity-80'
        )}
        style={{ flexWrap: 'wrap' }}
        accessibilityRole="button"
        accessibilityLabel={`View linked conversation: ${subject || shortId}`}>
        <HStack className="flex-wrap items-center gap-x-1.5 gap-y-1">
          <Icon as={Link2} size="sm" className="shrink-0 text-typography-600" />
          <Text
            size="sm"
            className={twJoin('shrink-0 text-typography-600', textClassName)}
            style={!isWeb ? { flexShrink: 0, fontSize: 14 } : undefined}>
            {label}:
          </Text>
          <Text
            size="sm"
            className={twJoin('shrink-0 font-medium text-typography-800', textClassName)}
            style={!isWeb ? { flexShrink: 0, fontSize: 14 } : undefined}>
            {subject || shortId}
          </Text>
          {participantsStr ? (
            <>
              {isWeb ? (
                <Text size="sm" className={twJoin('shrink-0 text-typography-500', textClassName)}>
                  —
                </Text>
              ) : null}
              <Text
                size="sm"
                className={twJoin('shrink-0 text-typography-700', textClassName)}
                style={!isWeb ? { flexShrink: 0, fontSize: 14 } : undefined}>
                {participantsStr}
              </Text>
            </>
          ) : null}
        </HStack>
        {errorSection}
      </Pressable>
    );
  }

  const contentClassName = groupViewWithContent
    ? 'w-full items-center gap-3'
    : 'w-full flex-1 items-center gap-3';

  return (
    <Pressable
      onPress={handlePress}
      className={twJoin(
        'w-full',
        containerClassName ??
          'items-center justify-start gap-3 border-t border-outline-100 bg-background-0 px-5 py-2 active:opacity-80 md:px-7'
      )}
      accessibilityRole="button"
      accessibilityLabel={`View linked conversation: ${subject || shortId}`}>
      <HStack className={contentClassName}>
        <Icon as={Link2} size="md" className="shrink-0 text-typography-600" />
        <VStack className="min-w-30 flex-1" style={!isWeb ? { flex: 1 } : undefined}>
          <HStack className="flex-row flex-wrap items-baseline gap-x-1">
            <Text size="sm" className="text-typography-600">
              {label}:
            </Text>
            <Text size="sm" className="font-medium text-typography-800">
              {subject || shortId}
            </Text>
          </HStack>
          {participantsSection}
        </VStack>
      </HStack>
      {errorSection}
    </Pressable>
  );
}
